import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { endOfWeek, startOfWeek } from 'date-fns'
import { groupBy, sortBy } from 'lodash'
import { filterDefaultValues } from 'components/configured-service/CreateDraftConfiguredServiceDialog/filterSettings'

/**
 * Local filter form state for ScheduleTabDraft and derived meta options.
 * - Creates isolated RHF instance for filters
 * - Wires external reset callback
 * - Only defaults week range and radius; autocomplete fields start empty
 * - Builds options arrays from metaFiltersData
 */
export function useScheduleFiltersForm({ metaFiltersData = {}, onRegisterFiltersReset, serviceStartDate }) {
  const filtersMethods = useForm({
    defaultValues: {
      startDate: startOfWeek(new Date(), { weekStartsOn: 0 }),
      endDate: endOfWeek(new Date(), { weekStartsOn: 0 }),
      radius: filterDefaultValues.radius,
      radiusCommitted: filterDefaultValues.radius,
      radiusUnit: filterDefaultValues.radiusUnit,
      measure: [],
      material: [],
      action: [],
      businessLine: [],
      selectedDays: {
        Sunday: true,
        Monday: true,
        Tuesday: true,
        Wednesday: true,
        Thursday: true,
        Friday: true,
        Saturday: true,
      },
    },
    mode: 'onChange',
  })

  // Register external reset hook for map filters
  useEffect(() => {
    if (!onRegisterFiltersReset) return

    const resetFilters = () => {
      const base = serviceStartDate ? startOfWeek(new Date(serviceStartDate), { weekStartsOn: 0 }) : startOfWeek(new Date(), { weekStartsOn: 0 })
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      const selectedDays = days.reduce((acc, d) => ({ ...acc, [d]: true }), {})

      filtersMethods.reset(
        {
          startDate: base,
          endDate: endOfWeek(base, { weekStartsOn: 0 }),
          radius: filterDefaultValues.radius,
          radiusCommitted: filterDefaultValues.radius,
          radiusUnit: filterDefaultValues.radiusUnit,
          measure: [],
          material: [],
          action: [],
          businessLine: [],
          selectedDays,
        },
        { keepDefaultValues: false }
      )
    }

    onRegisterFiltersReset(() => resetFilters())
    return () => {
      onRegisterFiltersReset(null)
    }
  }, [onRegisterFiltersReset, filtersMethods, serviceStartDate])

  // Build options from meta
  const { businessLines = [], materials = [], measure = [], serviceAction = [] } = metaFiltersData || {}

  const businessLineOptions = useMemo(
    () =>
      sortBy(
        (businessLines || []).filter(({ active }) => active).map(bl => ({ label: bl.businessLineLabel, value: bl.businessLineLabel })),
        'label'
      ),
    [businessLines]
  )

  const materialOptions = useMemo(
    () => sortBy(materials?.map(m => ({ label: m.materialType, value: m.materialType })) || [], 'label'),
    [materials]
  )

  const measureOptions = useMemo(() => {
    const options =
      measure?.map(m => ({
        label: `${m.volume} ${m.unit}`,
        value: `${m.volume} ${m.unit}`,
        volume: m.volume,
        unit: m.unit,
      })) || []
    const groupedOptions = groupBy(options, 'unit')
    const sortedUnits = Object.keys(groupedOptions).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    return sortedUnits.map(unit => sortBy(groupedOptions[unit], item => parseFloat(item.volume))).flat()
  }, [measure])

  const serviceActionOptions = useMemo(
    () => sortBy(serviceAction?.map(sa => ({ label: sa.actionType, value: sa.actionType })) || [], 'label'),
    [serviceAction]
  )

  return {
    filtersMethods,
    businessLineOptions,
    materialOptions,
    measureOptions,
    serviceActionOptions,
  }
}
